// Library Methods
import { useMemo } from "react";

// Components
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import { useTheme } from "@mui/material/styles";

// Styles
import { getSurfaceTokens } from "src/styles/mui/muiStylesHelper";

type SharedTabsType = {
  tabs: Array<{ id: number; label: string; showTab: boolean }>;
  selectedTabId: number;
  onChange: (id: number) => void;
  showBorder?: boolean;
  tabColor?: string;
};

function a11yProps(tabId: number) {
  return {
    id: `shared-tab-${tabId}`,
    "aria-controls": `shared-tabpanel-${tabId}`,
  };
}

const SharedTabs = ({
  tabs,
  selectedTabId,
  onChange,
  showBorder = true,
  tabColor = "#E2772E",
}: SharedTabsType) => {
  const theme = useTheme();
  const { onSurface } = getSurfaceTokens(theme);

  // if showTab is false, filter out tab
  const activeTabs = useMemo(() => tabs?.filter((tab) => tab.showTab), [tabs]);

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "flex-start",
        gap: "8px",
        borderBottom: showBorder ? `1px solid ${tabColor}` : "none",
        paddingBottom: "8px",
      }}
    >
      {activeTabs.map((tab) => (
        <Button
          key={tab.id}
          onClick={() => onChange(tab.id)}
          sx={{
            minWidth: 0,
            maxWidth: "100%",
            color: selectedTabId === tab.id ? "#FFF" : onSurface,
            backgroundColor:
              selectedTabId === tab.id ? tabColor : "transparent",
            padding: "4px 8px",
            border: "none",
            borderRadius: "4px",
            "&:hover": {
              color: selectedTabId === tab.id ? "#FFF" : onSurface,
              backgroundColor:
                selectedTabId === tab.id ? tabColor : "transparent",
              border: "none",
            },
            "&.Mui-disabled": {
              color: selectedTabId === tab.id ? "#FFF" : onSurface,
              backgroundColor:
                selectedTabId === tab.id ? tabColor : "transparent",
              border: "none",
            },
          }}
          {...a11yProps(tab.id)}
        >
          <Typography
            maxWidth="100%"
            whiteSpace="nowrap"
            overflow="hidden"
            textOverflow="ellipsis"
            fontSize="14px"
          >
            {tab.label}
          </Typography>
        </Button>
      ))}
    </Box>
  );
};

export default SharedTabs;
